import { PSIG_TO_INHG_FACTOR } from '../shared/constants';
import { createResolvePivotKey } from '../shared/resolve-pivot-key';
import { NormalizedUnitValue } from '../shared/types';
import { fpDivide, fpMultiply, isRawGaugePsiKey, toNumber } from '../shared/utils';

/** Gauge vs absolute vs differential reference frame for pressure units. */
export type PressureReference = 'gauge' | 'absolute' | 'differential';

/**
 * Authoritative pressure reference frames for every catalogued `transformPressure` unit.
 * Must stay in sync with catalog asciiSymbols (enforced in registry.test.ts).
 */
export const PRESSURE_REFERENCE_BY_SYMBOL: Record<string, PressureReference> = {
  psi: 'gauge',
  psig: 'gauge',
  inhg: 'gauge',
  inh2o: 'gauge',
  psia: 'absolute',
  psid: 'differential',
  bar: 'absolute',
  mbar: 'absolute',
  kpa: 'absolute',
  mpa: 'absolute',
  pa: 'absolute',
  mmhg: 'absolute',
  torr: 'absolute',
  atm: 'absolute',
};

const PA_PER_MMHG_OR_TORR = 133.322;

/** Pascals per pound-force per square inch (absolute scale). */
export const PA_PER_PSI = 6894.76;

/** Standard atmosphere in pascals — matches `atm: 101325` in PRESSURE_PIVOT. */
export const ATM_PA = 101325;

/** Standard atmosphere in psia — matches `atm: 101325` Pa in PRESSURE_PIVOT. */
export const PSI_PER_ATM = 14.6959;

/**
 * Pivot = Pa (absolute pressure). Gauge-scale keys (`psi`, `psig`) are intentionally
 * absent — they use PA_PER_PSI with reference-frame offsets instead.
 */
export const PRESSURE_PIVOT = {
  pa: 1,
  bar: 100000,
  mbar: 100,
  kpa: 1000,
  mpa: 1e6,
  psia: PA_PER_PSI,
  psid: PA_PER_PSI,
  inh2o: 249.089,
  mmhg: PA_PER_MMHG_OR_TORR,
  torr: PA_PER_MMHG_OR_TORR,
  atm: ATM_PA,
  inhg: 3386.39,
} as const;

const resolvePressurePivotKey = createResolvePivotKey(PRESSURE_PIVOT);

function getPressureReference(unitKey: string | undefined): PressureReference {
  if (!unitKey) {
    return 'absolute';
  }

  const catalogRef = PRESSURE_REFERENCE_BY_SYMBOL[unitKey.toLowerCase()];
  return catalogRef ?? 'absolute';
}

function gaugeInhgToPsig(inhg: number): number {
  return -1 * fpMultiply(inhg, 1 / PSIG_TO_INHG_FACTOR);
}

function gaugePsigToInhg(psig: number): number {
  return -1 * fpMultiply(psig, PSIG_TO_INHG_FACTOR);
}

function gaugePsigToAbsolutePa(psig: number): number {
  return fpMultiply(psig, PA_PER_PSI) + ATM_PA;
}

function absolutePaToGaugePsig(pa: number): number {
  return fpDivide(pa - ATM_PA, PA_PER_PSI);
}

function linearToAbsolutePa(value: number, unitKey: string): number | undefined {
  const factor = PRESSURE_PIVOT[unitKey as keyof typeof PRESSURE_PIVOT];
  if (factor == null) {
    return undefined;
  }
  return fpMultiply(value, factor);
}

function absolutePaToLinear(pa: number, unitKey: string): number | undefined {
  const factor = PRESSURE_PIVOT[unitKey as keyof typeof PRESSURE_PIVOT];
  if (factor == null) {
    return undefined;
  }
  return fpDivide(pa, factor);
}

function sourceToAbsolutePa(
  value: number,
  sourceKey: string,
  sourceRef: PressureReference,
  targetRef: PressureReference
): number | undefined {
  const skipAtmOffset = sourceRef === 'differential' || targetRef === 'differential';

  // inhg is catalogued as gauge — always vacuum-decode, never linear PRESSURE_PIVOT scaling.
  if (sourceKey === 'inhg') {
    const psig = gaugeInhgToPsig(value);
    if (skipAtmOffset) {
      return fpMultiply(psig, PA_PER_PSI);
    }
    return gaugePsigToAbsolutePa(psig);
  }

  if (isRawGaugePsiKey(sourceKey) && sourceRef === 'gauge') {
    if (skipAtmOffset) {
      return fpMultiply(value, PA_PER_PSI);
    }
    return gaugePsigToAbsolutePa(value);
  }

  const linearPa = linearToAbsolutePa(value, sourceKey);
  if (linearPa == null) {
    return undefined;
  }

  if (sourceRef === 'gauge' && !skipAtmOffset) {
    return linearPa + ATM_PA;
  }

  return linearPa;
}

function absolutePaToTarget(
  pa: number,
  targetKey: string,
  sourceRef: PressureReference,
  targetRef: PressureReference
): number | undefined {
  const skipAtmOffset = sourceRef === 'differential' || targetRef === 'differential';

  // inhg is catalogued as gauge — always vacuum-encode, never linear PRESSURE_PIVOT scaling.
  if (targetKey === 'inhg') {
    const psig = skipAtmOffset ? fpDivide(pa, PA_PER_PSI) : absolutePaToGaugePsig(pa);
    return gaugePsigToInhg(psig);
  }

  if (isRawGaugePsiKey(targetKey) && targetRef === 'gauge') {
    return skipAtmOffset ? fpDivide(pa, PA_PER_PSI) : absolutePaToGaugePsig(pa);
  }

  if (targetRef === 'gauge' && !skipAtmOffset) {
    return absolutePaToLinear(pa - ATM_PA, targetKey);
  }

  return absolutePaToLinear(pa, targetKey);
}

/**
 * Converts pressure honoring gauge/absolute/differential reference frames.
 * All conversions route through absolute Pa as the internal pivot.
 */
export function transformPressure(
  unitValue: NormalizedUnitValue,
  newUnits?: string
): NormalizedUnitValue {
  const value = toNumber(unitValue.value);
  if (value == null || !newUnits) {
    return unitValue;
  }

  const sourceKey = resolvePressurePivotKey(unitValue.units);
  const targetKey = resolvePressurePivotKey(newUnits);

  if (!sourceKey && !isRawGaugePsiKey(unitValue.units)) {
    return unitValue;
  }

  const resolvedSourceKey =
    sourceKey ?? (isRawGaugePsiKey(unitValue.units) ? unitValue.units.toLowerCase() : undefined);
  const resolvedTargetKey =
    targetKey ?? (isRawGaugePsiKey(newUnits) ? newUnits.toLowerCase() : undefined);

  if (!resolvedSourceKey || !resolvedTargetKey) {
    return unitValue;
  }

  // Same-unit identity: skip the absolute-Pa round-trip so gauge offsets
  // (ATM_PA) cannot introduce float drift on write-back (e.g. 12.3 psig → psig).
  if (resolvedSourceKey === resolvedTargetKey) {
    return { value, units: newUnits };
  }

  const sourceRef = getPressureReference(resolvedSourceKey);
  const targetRef = getPressureReference(resolvedTargetKey);

  const absolutePa = sourceToAbsolutePa(value, resolvedSourceKey, sourceRef, targetRef);
  if (absolutePa == null) {
    return unitValue;
  }

  const targetValue = absolutePaToTarget(absolutePa, resolvedTargetKey, sourceRef, targetRef);
  if (targetValue == null) {
    return unitValue;
  }

  return { value: targetValue, units: newUnits };
}
